import { ChevronRight, Layers, Lightbulb } from 'lucide-react';
import { useState } from 'react';
import type { DepthLayer } from '~/lib/feature-modules/types';
import { cn } from '~/lib/utils';
import TradeoffPanel from './TradeoffPanel';

interface Props {
  layers: DepthLayer[];
}

export default function DepthExplorer({ layers }: Props) {
  return (
    <div className="space-y-2">
      {layers.map((layer) => (
        <DepthNode key={layer.id} layer={layer} level={0} />
      ))}
    </div>
  );
}

function DepthNode({ layer, level }: { layer: DepthLayer; level: number }) {
  const [open, setOpen] = useState(false);

  const depthColors = [
    'border-violet-200 bg-violet-50/40',
    'border-purple-200 bg-purple-50/40',
    'border-fuchsia-200 bg-fuchsia-50/40',
  ];
  const colorClass = depthColors[level % depthColors.length];

  const headerColors = [
    'text-violet-700',
    'text-purple-700',
    'text-fuchsia-700',
  ];
  const headerColor = headerColors[level % headerColors.length];

  return (
    <div className={cn('border rounded-xl overflow-hidden', colorClass)}>
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="w-full text-left px-4 py-3 flex items-start gap-2.5 hover:bg-white/40 transition-colors"
      >
        <ChevronRight
          className={cn(
            'h-4 w-4 mt-0.5 flex-shrink-0 transition-transform',
            headerColor,
            open && 'rotate-90',
          )}
        />
        <div className="flex-1 min-w-0">
          <p className={cn('text-sm font-semibold', headerColor)}>
            {layer.label}
          </p>
          <p className="text-xs text-gray-600 mt-0.5">{layer.summary}</p>
        </div>
        {layer.children && layer.children.length > 0 && (
          <span className="flex-shrink-0 inline-flex items-center gap-1 text-[10px] font-medium text-gray-400 bg-white/60 rounded-full px-2 py-0.5">
            <Layers className="h-3 w-3" />
            {layer.children.length} deeper
          </span>
        )}
      </button>

      {open && (
        <div className="border-t border-inherit px-4 py-3 space-y-4">
          <p className="text-sm text-gray-700 leading-relaxed">
            {layer.context}
          </p>

          {layer.approaches && layer.approaches.length > 0 && (
            <div>
              <h5 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
                Approaches
              </h5>
              <div className="space-y-2">
                {layer.approaches.map((approach) => (
                  <div
                    key={approach.label}
                    className="bg-white rounded-lg border border-gray-200 px-3 py-2.5"
                  >
                    <p className="text-sm font-medium text-gray-900">
                      {approach.label}
                    </p>
                    <p className="text-xs text-gray-600 mt-0.5">
                      {approach.description}
                    </p>
                    {approach.bestWhen && (
                      <div className="flex items-start gap-1.5 mt-1.5">
                        <Lightbulb className="h-3 w-3 text-amber-500 mt-0.5 flex-shrink-0" />
                        <p className="text-xs text-amber-700">
                          {approach.bestWhen}
                        </p>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {layer.tradeoffs && layer.tradeoffs.length > 0 && (
            <div>
              <h5 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
                Tradeoffs at this level
              </h5>
              <TradeoffPanel tradeoffs={layer.tradeoffs} />
            </div>
          )}

          {layer.children && layer.children.length > 0 && (
            <div>
              <h5 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
                Go deeper
              </h5>
              <div className="space-y-2">
                {layer.children.map((child) => (
                  <DepthNode key={child.id} layer={child} level={level + 1} />
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
